import { create } from "zustand";
import {
  type Band,
  type SlotRole,
  type StageConfig,
  listBands,
  getBand,
  createBand as apiCreate,
  renameBand as apiRename,
  upsertBandSlot,
  removeBandSlot,
  deleteBand as apiDelete,
  updateBandStage,
} from "../infra/api.js";

interface BandState {
  bands: Band[];
  loading: boolean;
  error: string | null;
  fetch: () => Promise<void>;
  refresh: (id: string) => Promise<Band>;
  create: (input: {
    name: string;
    slots: { slot_role: SlotRole; artist_id: string }[];
  }) => Promise<Band>;
  rename: (id: string, name: string) => Promise<Band>;
  setSlot: (id: string, role: SlotRole, artistId: string) => Promise<Band>;
  removeSlot: (id: string, role: SlotRole) => Promise<Band>;
  updateStage: (id: string, config: StageConfig) => Promise<Band>;
  remove: (id: string) => Promise<void>;
}

export const useBandStore = create<BandState>((set, get) => ({
  bands: [],
  loading: false,
  error: null,

  async fetch() {
    set({ loading: true, error: null });
    try {
      const { bands } = await listBands();
      set({ bands, loading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
    }
  },

  async refresh(id) {
    const { band } = await getBand(id);
    set({ bands: get().bands.map((b) => (b.id === id ? band : b)) });
    return band;
  },

  async create(input) {
    const { band } = await apiCreate(input);
    set({ bands: [band, ...get().bands] });
    return band;
  },

  async rename(id, name) {
    const { band } = await apiRename(id, name);
    set({ bands: get().bands.map((b) => (b.id === id ? band : b)) });
    return band;
  },

  async setSlot(id, role, artistId) {
    const { band } = await upsertBandSlot(id, role, artistId);
    set({ bands: get().bands.map((b) => (b.id === id ? band : b)) });
    return band;
  },

  async removeSlot(id, role) {
    const { band } = await removeBandSlot(id, role);
    set({ bands: get().bands.map((b) => (b.id === id ? band : b)) });
    return band;
  },

  async updateStage(id, config) {
    const { band } = await updateBandStage(id, config);
    set({ bands: get().bands.map((b) => (b.id === id ? band : b)) });
    return band;
  },

  async remove(id) {
    await apiDelete(id);
    set({ bands: get().bands.filter((b) => b.id !== id) });
  },
}));
